import { FREE_INSTRUMENT_LABEL } from '@/constants';
import { createId, createTableRow, createTextBlock } from '@/lib/table-utils';
import { DEFAULT_COLUMN_WIDTHS } from '../test-table-editor/constants';
import type { TableBlock, TestBlock, TextBlock } from '@/types/report-payload';

/**
 * The width budget a pasted table's columns share, and the head start the first
 * one gets.
 *
 * `TableRenderer` renders the table `w-full table-fixed` inside a `min-w-full`
 * box, so column widths behave as proportions *while they fit* and as hard
 * pixels the moment their total overflows the page — at which point the surplus
 * is clipped off the right edge of the A4. A per-column constant therefore
 * works up to four columns and silently loses the fifth; a shared budget fits
 * any number of them.
 *
 * The budget is `DEFAULT_COLUMN_WIDTHS`' own total and the lead is that
 * constant's own 2:1, so a three-column paste lands on exactly the widths a
 * table created by hand already gets.
 */
const COLUMN_WIDTH_BUDGET = DEFAULT_COLUMN_WIDTHS.reduce((total, width) => total + width, 0);
const FIRST_COLUMN_WEIGHT = 2;

/** Splits {@link COLUMN_WIDTH_BUDGET} across `columnCount` columns. */
export const distributeColumnWidths = (columnCount: number): number[] => {
    if (columnCount <= 0) return [];

    const totalWeight = FIRST_COLUMN_WEIGHT + (columnCount - 1);
    const unit = COLUMN_WIDTH_BUDGET / totalWeight;

    return Array.from({ length: columnCount }, (_, index) =>
        Math.round(index === 0 ? unit * FIRST_COLUMN_WEIGHT : unit),
    );
};

/**
 * Builds a table block from a parsed clipboard grid. The first row becomes the
 * header, the rest become data rows.
 *
 * `order` is a placeholder — the insertion path re-derives it via `reindexBlocks`.
 */
export const tableBlockFromGrid = (grid: string[][]): TableBlock => {
    const headerValues = grid[0] ?? [];
    const dataRows = grid.slice(1);
    const columnCount = headerValues.length;

    return {
        id: createId('block'),
        block_type: 'table',
        order: 0,
        has_content: true,
        automatic: false,
        description: '',
        header: createTableRow(columnCount, headerValues),
        rows: dataRows.map((rowValues) => createTableRow(columnCount, rowValues)),
        columnWidths: distributeColumnWidths(columnCount),
    };
};

/** Builds a text block holding already-sanitized HTML (see `clipboardToHtml`). */
export const textBlockFromHtml = (html: string): TextBlock => ({
    id: createId('block'),
    block_type: 'text',
    order: 0,
    has_content: true,
    automatic: false,
    content: html,
});

/**
 * The instrument group a pasted table becomes: the table itself plus the
 * linked interpretation block "Interpretar com IA" writes into — the same
 * shape `buildFreeInstrumentBlocks` produces from the form.
 *
 * The instrument is `free_mode` for the same reason the form's is: a pasted
 * table has no catalog entry behind it, so its citation has to be written by
 * hand. Both are collected in the paste dialog and both are optional; a table
 * confirmed without a name opens under the default caption, editable on the
 * canvas like any other.
 */
export const instrumentBlocksFromGrid = (
    grid: string[][],
    identity: { name: string; reference: string },
): TestBlock[] => {
    const table = tableBlockFromGrid(grid);
    const name = identity.name.trim();
    const reference = identity.reference.trim();

    const instrumentTable: TableBlock = {
        ...table,
        description: name || FREE_INSTRUMENT_LABEL,
        instrument_source: 'free_mode',
        // Omitted rather than stored empty, so a report without a citation has
        // the same payload it had before the field existed.
        ...(reference ? { instrument_reference: reference } : {}),
        // A grid of nothing but a header row would leave the table rowless, and
        // a rowless table renders as the "selecione instrumentos" placeholder
        // (TestTable) — swallowing the columns the user just pasted. One blank
        // row makes it open as a real, editable table instead.
        rows: table.rows.length > 0 ? table.rows : [createTableRow(table.columnWidths.length)],
    };

    return [instrumentTable, createTextBlock(instrumentTable.id)];
};
